import cn from 'clsx'
import { Fragment, ReactNode } from 'react'
import { Menu, Transition } from '@headlessui/react'
import Link from 'components/ui/Link'
import { sendEvent } from '@lib/tracking'

export interface DropdownItem {
  label: string
  disabled: boolean
  href?: string
}
export interface DropdownProps {
  items: DropdownItem[]
  selectedLabel: string
  label: string
  onSelect?: (index: number) => void
  className?: string
  labelClassName?: string
}

interface DropdownItemElementProps
  extends React.ButtonHTMLAttributes<HTMLElement> {
  item: DropdownItem
  children: ReactNode
}
const DropdownItemElement = ({
  item,
  children,
  ...props
}: DropdownItemElementProps) =>
  item.href === undefined ? (
    <button {...props}>{children}</button>
  ) : (
    <Link href={item.href} {...props}>
      {children}
    </Link>
  )

export const Dropdown = ({
  items,
  selectedLabel,
  label,
  onSelect = () => {},
  className,
  labelClassName,
}: DropdownProps) => {
  const handleOnClick = (item: DropdownItem, index: number) => {
    onSelect(index)
    sendEvent({
      event: 'Click_My_Account_Link',
      data: {
        page_path: window.location.pathname,
        click_url: item.href ?? '',
      },
    })
  }

  return (
    <Menu
      as="div"
      className={cn('relative border-deepPurple text-left flex', className)}
    >
      {({ open }) => (
        <>
          <div>
            <Menu.Button className="w-full flex items-center">
              <span
                className={cn(
                  'uppercase mr-2 whitespace-nowrap',
                  labelClassName
                )}
              >
                {label}:
              </span>
              <div className="max-w-[150px] whitespace-nowrap text-ellipsis overflow-hidden">
                <span className="font-bold mr-3 text-right">
                  {selectedLabel}
                </span>
              </div>
              <div
                className={cn('transition-all text-xl', { 'rotate-90': open })}
              >
                {'>'}
              </div>
            </Menu.Button>
          </div>
          <Transition
            show={open}
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute flex flex-col right-0 origin-top-right w-full justify-between z-10 border border-deepPurple">
              {items.map((item, index) => (
                <Menu.Item key={item.label} disabled={item.disabled}>
                  {({ active }) => (
                    <DropdownItemElement
                      item={item}
                      onClick={() => handleOnClick(item, index)}
                      className={cn(
                        'group bg-superDark flex items-center min-h-[2.25rem] px-2 border-b border-deepPurple',
                        {
                          'bg-deepPurple':
                            active || item.label === selectedLabel,
                          'border-t-0': index === 0,
                          'border-b-0': index === items.length - 1,
                        }
                      )}
                    >
                      <span
                        className={cn(
                          'text-left w-full whitespace-nowrap text-ellipsis overflow-hidden',
                          {
                            'text-grey cursor-default': item.disabled,
                            'text-white': !item.disabled,
                          }
                        )}
                      >
                        {item.label}
                      </span>
                    </DropdownItemElement>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  )
}
